export interface RuntimeAuthConfig {
    authority: string;
    clientId: string;
    audience: string;
}

let runtimeAuth: RuntimeAuthConfig | undefined;

export function parseRuntimeAuthConfig(value: unknown): RuntimeAuthConfig | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Invalid authentication configuration.');
    }
    if (Object.keys(value).length === 0) return undefined;
    const config = value as Record<string, unknown>;
    if (Object.keys(config).some(key => !['authority', 'clientId', 'audience'].includes(key))
        || typeof config['authority'] !== 'string'
        || typeof config['clientId'] !== 'string'
        || typeof config['audience'] !== 'string') {
        throw new Error('Incomplete authentication configuration.');
    }
    const authority = new URL(config['authority']);
    if (authority.protocol !== 'https:' || authority.username || authority.password
        || authority.search || authority.hash || authority.pathname !== '/'
        || !/^[A-Za-z0-9_-]+$/.test(config['clientId'])
        || !config['audience'] || /\s/.test(config['audience'])) {
        throw new Error('Invalid authentication configuration.');
    }
    return {
        authority: authority.origin,
        clientId: config['clientId'],
        audience: config['audience']
    };
}

// Loaded before importing app.config or bootstrapping Angular. A missing/broken
// response stops startup instead of silently sending a testing user to production.
export async function loadRuntimeAuthConfig(baseUri: string, fetchConfig: typeof fetch = fetch): Promise<void> {
    const response = await fetchConfig(new URL('assets/runtime-auth.json', baseUri), {
        cache: 'no-store', credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Authentication configuration could not load.');
    runtimeAuth = parseRuntimeAuthConfig(await response.json());
}

export function withRuntimeAuth<T extends {
    authority: string;
    clientId: string;
    customParamsAuthRequest?: Record<string, unknown>;
}>(config: T, overrides = runtimeAuth): T {
    if (!overrides) return config;
    return {
        ...config,
        authority: overrides.authority,
        authWellknownEndpointUrl: `${overrides.authority}/.well-known/openid-configuration`,
        clientId: overrides.clientId,
        customParamsAuthRequest: { ...config.customParamsAuthRequest, audience: overrides.audience }
    };
}
